"use client";

import { useEffect, useState, type ReactNode } from "react";
import type { Language, Platform } from "@/lib/dramahub";
import { GridPicker } from "./grid-picker";

const ignoreSelection = () => {};

export function Header({ onSelectionChange = ignoreSelection, children }: { onSelectionChange?: (platform: string, lang: string) => void; children?: ReactNode }) {
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [language, setLanguage] = useState("");
  const [languageLoading, setLanguageLoading] = useState(false);
  const [languageError, setLanguageError] = useState(false);
  const [languageAttempt, setLanguageAttempt] = useState(0);

  useEffect(() => {
    const openPlatforms = () => {
      if (window.location.hash !== "#platform" || loading || error) return;
      const button = document.getElementById("platform") as HTMLButtonElement | null;
      if (button && !button.disabled && button.getAttribute("aria-expanded") !== "true") button.click();
    };
    openPlatforms();
    window.addEventListener("hashchange", openPlatforms);
    return () => window.removeEventListener("hashchange", openPlatforms);
  }, [loading, error]);

  useEffect(() => {
    onSelectionChange(selected, languageLoading || languageError ? "" : language);
  }, [selected, language, languageLoading, languageError, onSelectionChange]);

  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError(false);
      try {
        const response = await fetch("/api/platforms", { signal: controller.signal });
        const body = await response.json();
        if (!response.ok || !body.success) throw new Error("Platforms unavailable");
        setPlatforms(body.data);
        let savedPlatform = "";
        try { savedPlatform = localStorage.getItem("dracin-platform") ?? ""; } catch {}
        setSelected(body.data.some((item: Platform) => item.platform === savedPlatform)
          ? savedPlatform
          : body.data.find((item: Platform) => item.platform === "vibeshort")?.platform ?? body.data[0]?.platform ?? "");
      } catch {
        if (!controller.signal.aborted) setError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    void load();
    return () => controller.abort();
  }, [attempt]);

  useEffect(() => {
    if (!selected) return;
    const controller = new AbortController();
    async function loadLanguages() {
      setLanguageLoading(true);
      setLanguageError(false);
      setLanguages([]);
      setLanguage("");
      try {
        const response = await fetch(`/api/languages?${new URLSearchParams({ platform: selected })}`, { signal: controller.signal });
        const body = await response.json();
        if (!response.ok || !body.success) throw new Error("Languages unavailable");
        if (controller.signal.aborted) return;
        setLanguages(body.data);
        let savedLanguage = "";
        try { savedLanguage = localStorage.getItem(`dracin-language:${selected}`) ?? ""; } catch {}
        setLanguage(body.data.some((item: Language) => item.lang === savedLanguage)
          ? savedLanguage
          : body.data.find((item: Language) => item.lang.toLowerCase() === "id" || item.lang.toLowerCase() === "id_id")?.lang ?? body.data[0]?.lang ?? "");
      } catch {
        if (!controller.signal.aborted) setLanguageError(true);
      } finally {
        if (!controller.signal.aborted) setLanguageLoading(false);
      }
    }
    void loadLanguages();
    return () => controller.abort();
  }, [selected, languageAttempt]);

  function changePlatform(platform: string) {
    setSelected(platform);
    try { localStorage.setItem("dracin-platform", platform); } catch {}
    setLanguages([]);
    setLanguage("");
    setLanguageError(false);
    setLanguageLoading(Boolean(platform));
  }

  function changeLanguage(value: string) {
    setLanguage(value);
    try { localStorage.setItem(`dracin-language:${selected}`, value); } catch {}
  }
  const current = platforms.find((item) => item.platform === selected);
  const placeholder = loading ? "Memuat platform..." : error ? "Platform tidak tersedia" : platforms.length ? "Pilih platform" : "Belum ada platform";

  return (
    <header className="site-header">
      <div className="header-inner">
        <a className="brand" href="/" aria-label="Dracin beranda">
          <span className="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none"><path d="m9 6 9 6-9 6V6Z" fill="currentColor" /><path d="M5 6v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </span>
          <span>dracin<span className="brand-dot">.</span></span>
        </a>
        {children}
        <div className="header-controls">
        <div className="platform-control">
          <label htmlFor="platform">JELAJAHI DARI</label>
          <GridPicker id="platform" title="Platform" value={selected} placeholder={placeholder}
            choices={platforms.map((item) => ({ value: item.platform, label: item.platform, detail: `${item.titles.toLocaleString("id-ID")} judul` }))}
            disabled={loading || error || !platforms.length} onChange={changePlatform} />
          <div id="platform-status" className="platform-status" role="status">
            {error ? <button onClick={() => setAttempt((value) => value + 1)}>Coba lagi</button> : current ? `${current.titles.toLocaleString("id-ID")} judul` : ""}
          </div>
        </div>
        <div className="language-control">
          <label className="sr-only" htmlFor="language">Bahasa</label>
          <GridPicker id="language" title="Bahasa" value={language}
            placeholder={languageLoading ? "Memuat..." : languageError ? "Gagal dimuat" : !selected ? "Bahasa" : "Tidak tersedia"}
            choices={languages.map((item) => ({ value: item.lang, label: item.lang.toUpperCase() }))}
            disabled={!selected || languageLoading || languageError || !languages.length} onChange={changeLanguage} />
          <div id="language-status" className="platform-status" role="status">
            {languageError && <button onClick={() => setLanguageAttempt((value) => value + 1)}>Coba lagi</button>}
          </div>
        </div>
        </div>
      </div>
    </header>
  );
}



